import { Column, Filter } from "@120wateraudit/envirio-components";
import { DocumentNode } from "graphql";
import { omit } from "lodash";
import React from "react";
import { GridProps } from "semantic-ui-react";
import { QueryHookOptions, ApolloClient, FetchPolicy } from "@apollo/client";

import { HooksGridContainer } from "./HooksGridContainer";


export interface FilterMap {
  [filterName: string]: Filter;
}

export interface GQLGridWithHooksProps {
  additionalMenuItems?: undefined | React.ReactNode;
  availableFilters: FilterMap;
  client?: ApolloClient<any>;
  columns: Column[];
  defaultFilters?: Filter[];
  defaultSorts?: { [key in string]: SortDirection };
  displaySearch?: boolean;
  entityName?: string;
  exportURL?: string;
  fetchPolicy?: FetchPolicy;
  fetchURL?: string;
  gridOptions?: Partial<GridProps>;
  noResultsFound?: JSX.Element;
  pageIndexBase?: number;
  parentRef?: any;
  pluralEntityName?: string;
  pollInterval?: number;
  query: DocumentNode;
  queryName: string;
  sorts?: { [key in string]: SortDirection };
  topRightFilter?: Filter;
  variables: QueryHookOptions["variables"];
  getGridFilters: (filters: { [key in string]: Filter["value"] }) => void;
  getSearchValue: (searchValue: string) => void;
  onFilterChanged?: (
    currentState: GQLGridState
  ) => undefined | Partial<GQLGridState>;
  queryVariablesTransformer?: (vars: any) => any;
  renderRow: (item: any, index?: number, columns?: Column[]) => React.ReactNode;
  totalGetter?(data: any): number;
}

interface GQLGridState {
  exportURL?: string;
  filterOperators: { [key in string]: string };
  filterValues2: { [key in string]: any };
  filters: { [key in string]: Filter["value"] };
  filtersArray?: Filter[];
  isFetching?: boolean;
  pageIndexBase: number;
  pageNumber: number;
  pageSize: number;
  searchValue: undefined | string;
  sorts: { [key in string]: SortDirection };
  topRightFilter: undefined | { [key in string]: Filter["value"] };
  valueToBeSearched: undefined | string;
}

export class GQLGridWithHooks extends React.Component<
  GQLGridWithHooksProps,
  GQLGridState
> {
  query: any;
  variables: any;
  refetch: any;

  constructor(props: GQLGridWithHooksProps) {
    super(props);

    if (props.parentRef) {
      props.parentRef(this);
    }

    const filters = {};

    let topRightFilter: GQLGridState["topRightFilter"];

    if (props.topRightFilter) {
      filters[props.topRightFilter.key] = props.topRightFilter.value;

      topRightFilter = {
        [props.topRightFilter.key]: props.topRightFilter.value,
      };
    }

    this.state = {
      filterOperators: {},
      filterValues2: {},
      filters,
      filtersArray: this.props.defaultFilters || [],
      pageIndexBase: this.props.pageIndexBase || 0,
      pageNumber: 0,
      pageSize: 10,
      searchValue: undefined,
      sorts: this.props.defaultSorts || {},
      topRightFilter,
      valueToBeSearched: undefined,
    };
  }

  handleColumnClicked = (refetch: () => void) => (column: Column) => {
    if (!column.sortable) {
      return;
    }

    const { sorts = {} } = this.state;

    if (sorts[column.key]) {
      if (sorts[column.key] === "ASC") {
        sorts[column.key] = "DESC";
      } else {
        delete sorts[column.key];
      }
    } else {
      sorts[column.key] = "ASC";
    }

    this.setState({ sorts }, refetch);
  };

  handlePageChanged =
    (refetch: () => void) =>
    (_, { activePage: page }: any) => {
      this.setState({ pageNumber: page - 1 }, refetch);
    };

  handlePageSizeChanged =
    (refetch: () => void) =>
    ({ value: pageSize }: { value: any }) => {
      this.setState({ pageNumber: 0, pageSize }, refetch);
    };

  handleTopRightFilterChanged = (refetch: () => void) => (_, value) => {
    const { topRightFilter } = this.props;

    if (topRightFilter) {
      this.setState(
        {
          filters: {
            ...this.state.filters,
            [topRightFilter.key]: value,
          },
          pageNumber: 0,
          topRightFilter: {
            [topRightFilter.key]: value,
          },
        },
        refetch
      );
    }
  };

  getExportVariables = () => {
    const { searchValue, sorts = {} } = this.state;

    return {
      search: searchValue,
      sorts,
    };
  };

  fetchData = async () => {
    this.forceUpdate();
  };

  public getVariables = () => {
    const { variables } = this.props;
    const {
      filters,
      pageNumber,
      pageSize,
      sorts,
      topRightFilter: topRightFilterState,
      valueToBeSearched,
    } = this.state;

    return {
      ...variables,
      params: {
        filters: {
          ...filters,
          ...(topRightFilterState || {}),
        },
        pageNumber,
        pageSize,
        search: valueToBeSearched,
        sorts,
      },
    };
  };

  public getRefetch = () => {
    if (this.refetch && typeof this.refetch === "function") {
      this.refetch();
    }
  };

  buildFetchURL = (exporting = false): string => {
    let sortStr = "";
    let outURL = exporting
      ? this.props.exportURL || ""
      : this.props.fetchURL || "";

    const { filtersArray, topRightFilter } = this.state;
    const { pageNumber, pageSize, searchValue, sorts } = this.state;
    const pageSizeStr = `&pageSize=${pageSize}`;
    const pageNumberStr = `&pageNumber=${
      pageNumber + this.state.pageIndexBase
    }`;
    const searchStr = searchValue ? `&search=${searchValue}` : "";
    const createdOn = topRightFilter
      ? `&createdOn=${topRightFilter.createdOn}`
      : "";

    if (sorts) {
      const sortArray: string[] = [];
      sortStr = "&sorts=";
      // tslint:disable-next-line:forin
      for (const key in sorts) {
        sortArray.push(`${key} ${sorts[key]}`);
      }

      sortStr += sortArray.join(",");
    }

    const includeQSStart = outURL ? outURL.indexOf("?") === -1 : undefined;

    outURL += `${
      includeQSStart ? "?" : ""
    }${searchStr}${sortStr}${pageSizeStr}${pageNumberStr}`;

    if (filtersArray && filtersArray.length > 0) {
      outURL += filtersArray.reduce((previous: string, filter: Filter) => {
        return (previous += `&${filter.key}=${filter.value}`);
      }, "");
    }

    outURL += createdOn;

    return outURL;
  };

  addFilterToFilterArray = (filter: Filter, filterValue: any) => {
    const { filtersArray } = this.state;
    if (Array.isArray(filtersArray)) {
      let newState: GQLGridState = {
        ...this.state,
        filtersArray: [
          ...filtersArray,
          {
            ...filter,
            value: filterValue,
          },
        ],
      };

      newState = this.addOnFilterChangedState(newState);

      this.setState(newState, () => {
        this.fetchData();
      });
    }
  };

  addOnFilterChangedState = (newState: GQLGridState): GQLGridState => {
    const { onFilterChanged } = this.props;

    if (typeof onFilterChanged === "function") {
      const filterChangeState = onFilterChanged(newState);

      if (typeof filterChangeState !== "undefined") {
        newState = {
          ...newState,
          ...filterChangeState,
        };
      }
    }

    return newState;
  };

  removeFilter = (filter: Filter) => {
    const { filtersArray } = this.state;

    if (filtersArray) {
      this.setState(
        {
          filtersArray: filtersArray.filter((f) => f.key !== filter.key),
        },
        () => {
          this.fetchData();
        }
      );
    }
  };

  getAvailableFilters = () => {
    const filterOptions = Object.values(this.props.availableFilters);
    return filterOptions.filter((f) => this.state.filters[f.key] === undefined);
  };

  getSelectedFilters = () => {
    const { availableFilters } = this.props;
    const { filterOperators, filterValues2, filters } = this.state;

    const filterOptions = Object.values(availableFilters);
    return filterOptions
      .filter((f) => filters.hasOwnProperty(f.key))
      .map((fo) => ({
        ...fo,
        label: fo.label,
        operator: filterOperators[fo.key],
        value: filters[fo.key],
        value2: filterValues2[fo.key],
      }));
  };

  handleFilterApplied =
    (refetch: () => void) =>
    (
      filter: Filter,
      value: any, // todo: type this
      operator: any, // todo: type this
      value2?: any // todo: code smell. needs better data type to support n values
    ) => {
      const { filters } = this.state;

      if (filter) {
        this.addFilterToFilterArray(filter, value);
      }

      this.setState(
        {
          filterOperators: {
            ...this.state.filterOperators,
            [filter.key]: operator,
          },
          filterValues2: {
            ...this.state.filterValues2,
            [filter.key]: value2,
          },
          filters: {
            ...filters,
            [filter.key]: value,
          },
          pageNumber: 0,
        },
        () => {
          this.props.getGridFilters(this.state.filters);
          return refetch;
        }
      );
    };

  handleFilterRemoved = (refetch: () => void) => (filter: Filter) => {
    const { filterOperators, filterValues2, filters, filtersArray } =
      this.state;
    if (filters) {
      this.setState(
        {
          filterOperators: omit(filterOperators, [filter.key]),
          filterValues2: omit(filterValues2, [filter.key]),
          filters: omit(filters, [filter.key]),
          filtersArray: filtersArray
            ? filtersArray.filter((f) => f.key !== filter.key)
            : [],
          pageNumber: 0,
        },
        () => {
          this.props.getGridFilters(this.state.filters);
          return refetch;
        }
      );
    }
  };

  render() {
    const {
      additionalMenuItems,
      client,
      columns,
      displaySearch,
      entityName,
      noResultsFound,
      pluralEntityName,
      pollInterval,
      query,
      queryName,
      queryVariablesTransformer,
      renderRow,
      topRightFilter,
      totalGetter,
      variables,
    } = this.props;

    const {
      filters,
      filtersArray,
      pageNumber,
      pageSize,
      sorts,
      topRightFilter: topRightFilterState,
      valueToBeSearched,
    } = this.state;
    return (
      <>
        <HooksGridContainer
          additionalMenuItems={additionalMenuItems}
          availableFilters={this.getAvailableFilters()}
          client={client}
          columns={columns}
          displaySearch={displaySearch}
          entityName={entityName || "item"}
          filters={filters}
          filtersArray={filtersArray}
          noResultsFound={noResultsFound}
          onColumnClicked={this.handleColumnClicked}
          onFilterApplied={this.handleFilterApplied}
          onFilterRemoveClicked={this.handleFilterRemoved}
          onPageChanged={this.handlePageChanged}
          onPageSizeChanged={this.handlePageSizeChanged}
          onTopRightFilterChanged={this.handleTopRightFilterChanged}
          page={pageNumber}
          pageSize={pageSize}
          pluralEntityName={pluralEntityName || "items"}
          pollInterval={pollInterval}
          query={query}
          queryName={queryName}
          queryVariablesTransformer={queryVariablesTransformer}
          renderRow={renderRow}
          searchValue={valueToBeSearched}
          selectedFilters={this.getSelectedFilters()}
          sorted={sorts}
          style={{ margin: "2rem 0" }}
          topRightFilter={
            topRightFilter && topRightFilterState
              ? {
                  ...topRightFilter,
                  value: topRightFilterState[topRightFilter.key],
                }
              : undefined
          }
          totalGetter={totalGetter}
          variables={variables}
        />
      </>
    );
  }
}

type SortDirection = "ASC" | "DESC";
